import React, { useState, useEffect, useCallback } from 'react';

/**
 * NegationGroupsAdmin — Self-contained admin panel for verb negation groups.
 *
 * Handles: don't, doesn't, do not, does not, can't, cannot, couldn't, shouldn't, wouldn't, won't
 * NOT yet: didn't, haven't, never (need separate verb forms)
 *
 * DB: verb_negation_groups in Grammar_DB_DEV
 * Backend: grammar_routes.py — CRUD endpoints
 * Pipeline: Stage 2.5 in db_driven_translate() -> _build_negation() in server.py
 */

const PRONOUNS = [
  { key: 'i', label: 'I' },
  { key: 'you_sm', label: 'you (S)(M)' },
  { key: 'you_sf', label: 'you (S)(F)' },
  { key: 'he', label: 'he' },
  { key: 'she', label: 'she' },
  { key: 'we', label: 'we' },
  { key: 'you_p', label: 'you (P)' },
  { key: 'they', label: 'they' }
];

const PRONOUN_KEYS = PRONOUNS.map(p => p.key);
const EMPTY_SUFFIXES = { i: '', you_sm: '', you_sf: '', he: '', she: '', we: '', you_p: '', they: '' };

const NegationGroupsAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', suffixes: { ...EMPTY_SUFFIXES }, suffixes_ar: { ...EMPTY_SUFFIXES } });
  const [editGroup, setEditGroup] = useState(null);
  const [editGroupName, setEditGroupName] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [addVerb, setAddVerb] = useState({ groupId: null, stem: '', english: '', fem_stem: '', alt_stem: '', stem_ar: '', fem_stem_ar: '', alt_stem_ar: '' });
  const [editVerb, setEditVerb] = useState(null); // { groupId, originalStem, ...fields }
  const [testStem, setTestStem] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const showConfirm = (opts) => new Promise((resolve) => {
    setConfirmDialog({
      ...opts,
      onConfirm: () => { setConfirmDialog(null); resolve(true); },
      onCancel: () => { setConfirmDialog(null); resolve(false); }
    });
  });

  // ── CRUD ──

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/grammar/negation-groups`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGroups(data.groups || []);
      }
    } catch (err) {
      console.error('Error fetching negation groups:', err);
    }
    setLoading(false);
  }, [API, adminToken]);

  const createGroup = async () => {
    if (!newGroup.name.trim()) return showNotification('Group name required', 'error');
    const missing = Object.entries(newGroup.suffixes).find(([, v]) => !v.trim());
    if (missing) return showNotification(`Suffix for '${missing[0]}' is required`, 'error');
    try {
      const res = await fetch(`${API}/admin/grammar/negation-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(newGroup)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showNotification('Group created', 'success');
        setNewGroup({ name: '', suffixes: { ...EMPTY_SUFFIXES }, suffixes_ar: { ...EMPTY_SUFFIXES } });
        fetchGroups();
      } else {
        showNotification(data.detail || 'Failed to create group', 'error');
      }
    } catch (err) {
      showNotification('Error creating group', 'error');
    }
  };

  const updateGroup = async (groupId, updateData) => {
    try {
      const res = await fetch(`${API}/admin/grammar/negation-groups/${groupId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(updateData)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showNotification('Group updated', 'success');
        setEditGroup(null);
        setEditGroupName(null);
        fetchGroups();
      } else {
        showNotification(data.detail || 'Failed to update group', 'error');
      }
    } catch (err) {
      showNotification('Error updating group', 'error');
    }
  };

  const deleteGroup = async (groupId) => {
    const confirmed = await showConfirm({ title: 'Delete Group', message: 'Remove all verbs first.', confirmText: 'Delete', confirmColor: 'red' });
    if (!confirmed) return;
    try {
      const res = await fetch(`${API}/admin/grammar/negation-groups/${groupId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${adminToken}` } });
      const data = await res.json();
      if (res.ok && data.success) { showNotification('Group deleted', 'success'); fetchGroups(); }
      else showNotification(data.detail || 'Failed', 'error');
    } catch { showNotification('Error deleting group', 'error'); }
  };

  const addVerbToGroup = async (groupId) => {
    if (!addVerb.stem.trim()) return showNotification('Verb stem required', 'error');
    try {
      const res = await fetch(`${API}/admin/grammar/negation-groups/${groupId}/verbs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({
          stem: addVerb.stem.trim(), english: addVerb.english.trim(),
          fem_stem: (addVerb.fem_stem || '').trim(), alt_stem: (addVerb.alt_stem || '').trim(),
          stem_ar: (addVerb.stem_ar || '').trim(), fem_stem_ar: (addVerb.fem_stem_ar || '').trim(),
          alt_stem_ar: (addVerb.alt_stem_ar || '').trim()
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showNotification(`Verb '${addVerb.stem}' added`, 'success');
        setAddVerb({ groupId: null, stem: '', english: '', fem_stem: '', alt_stem: '', stem_ar: '', fem_stem_ar: '', alt_stem_ar: '' });
        fetchGroups();
      } else {
        showNotification(data.detail || 'Failed to add verb', 'error');
      }
    } catch { showNotification('Error adding verb', 'error'); }
  };

  const saveEditVerb = async () => {
    if (!editVerb) return;
    try {
      const res = await fetch(`${API}/admin/grammar/negation-groups/${editVerb.groupId}/verbs/${encodeURIComponent(editVerb.originalStem)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({
          stem: editVerb.stem, english: editVerb.english,
          fem_stem: editVerb.fem_stem || '', alt_stem: editVerb.alt_stem || '',
          stem_ar: editVerb.stem_ar || '', fem_stem_ar: editVerb.fem_stem_ar || '',
          alt_stem_ar: editVerb.alt_stem_ar || '',
          forms_ar: editVerb.forms_ar || {},
          forms_latin: editVerb.forms_latin || {}
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showNotification('Verb updated', 'success');
        setEditVerb(null);
        fetchGroups();
      } else {
        showNotification(data.detail || 'Failed to update verb', 'error');
      }
    } catch { showNotification('Error updating verb', 'error'); }
  };

  const removeVerb = async (groupId, stem) => {
    const confirmed = await showConfirm({ title: 'Remove Verb', message: `Remove '${stem}'?`, confirmText: 'Remove', confirmColor: 'red' });
    if (!confirmed) return;
    try {
      const res = await fetch(`${API}/admin/grammar/negation-groups/${groupId}/verbs/${encodeURIComponent(stem)}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${adminToken}` } });
      const data = await res.json();
      if (res.ok && data.success) { showNotification(`Verb '${stem}' removed`, 'success'); fetchGroups(); }
      else showNotification(data.detail || 'Failed', 'error');
    } catch { showNotification('Error removing verb', 'error'); }
  };

  const testNegation = async (stem) => {
    try {
      const res = await fetch(`${API}/admin/grammar/negation-groups/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify({ stem })
      });
      const data = await res.json();
      if (res.ok && data.success) setTestResult(data);
      else { showNotification(data.detail || 'Not found', 'error'); setTestResult(null); }
    } catch { showNotification('Error testing', 'error'); }
  };

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  // ── Helpers ──
  const dk = isDarkMode;
  const inputCls = `w-full px-2 py-1 rounded border text-sm ${dk ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`;
  const inputClsAr = `w-full px-2 py-1 rounded border text-sm text-right ${dk ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`;

  return (
    <div className={`p-6 rounded-lg border ${dk ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>

      {/* Confirm Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`p-6 rounded-lg max-w-sm ${dk ? 'bg-gray-800' : 'bg-white'}`}>
            <h4 className={`font-bold mb-2 ${dk ? 'text-white' : 'text-gray-800'}`}>{confirmDialog.title}</h4>
            <p className={`mb-4 text-sm ${dk ? 'text-gray-300' : 'text-gray-600'}`}>{confirmDialog.message}</p>
            <div className="flex gap-2 justify-end">
              <button onClick={confirmDialog.onCancel} className={`px-3 py-1 rounded ${dk ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}>Cancel</button>
              <button onClick={confirmDialog.onConfirm} className={`px-3 py-1 rounded text-white ${confirmDialog.confirmColor === 'red' ? 'bg-red-600' : 'bg-emerald-600'}`}>{confirmDialog.confirmText || 'OK'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Verb Modal */}
      {editVerb && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`p-6 rounded-lg w-full max-w-lg ${dk ? 'bg-gray-800' : 'bg-white'}`}>
            <h4 className={`font-bold mb-4 ${dk ? 'text-white' : 'text-gray-800'}`}>Edit Verb: {editVerb.english || editVerb.originalStem}</h4>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className={`text-xs font-medium ${dk ? 'text-gray-400' : 'text-gray-500'}`}>English</label>
                <input type="text" value={editVerb.english} onChange={e => setEditVerb({ ...editVerb, english: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={`text-xs font-medium ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Stem (Latin)</label>
                <input type="text" value={editVerb.stem} onChange={e => setEditVerb({ ...editVerb, stem: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={`text-xs font-medium ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Fem stem (Latin)</label>
                <input type="text" value={editVerb.fem_stem || ''} onChange={e => setEditVerb({ ...editVerb, fem_stem: e.target.value })} placeholder="optional" className={inputCls} />
              </div>
              <div>
                <label className={`text-xs font-medium ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Alt stem (Latin)</label>
                <input type="text" value={editVerb.alt_stem || ''} onChange={e => setEditVerb({ ...editVerb, alt_stem: e.target.value })} placeholder="optional" className={inputCls} />
              </div>
              <div>
                <label className={`text-xs font-medium ${dk ? 'text-amber-400' : 'text-amber-600'}`}>Stem (Arabic)</label>
                <input type="text" dir="rtl" value={editVerb.stem_ar || ''} onChange={e => setEditVerb({ ...editVerb, stem_ar: e.target.value })} className={inputClsAr} />
              </div>
              <div>
                <label className={`text-xs font-medium ${dk ? 'text-amber-400' : 'text-amber-600'}`}>Fem stem (Arabic)</label>
                <input type="text" dir="rtl" value={editVerb.fem_stem_ar || ''} onChange={e => setEditVerb({ ...editVerb, fem_stem_ar: e.target.value })} className={inputClsAr} />
              </div>
              <div>
                <label className={`text-xs font-medium ${dk ? 'text-amber-400' : 'text-amber-600'}`}>Alt stem (Arabic)</label>
                <input type="text" dir="rtl" value={editVerb.alt_stem_ar || ''} onChange={e => setEditVerb({ ...editVerb, alt_stem_ar: e.target.value })} className={inputClsAr} />
              </div>
            </div>
            {/* Per-pronoun Latin overrides */}
            <div className={`mb-4 p-3 rounded ${dk ? 'bg-gray-700' : 'bg-emerald-50'}`}>
              <p className={`text-xs font-medium mb-2 ${dk ? 'text-emerald-400' : 'text-emerald-600'}`}>Latin overrides per pronoun (full form with ma — only fill if formula is wrong):</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {PRONOUNS.map(({ key, label }) => (
                  <div key={`fl-${key}`}>
                    <label className={`text-xs ${dk ? 'text-emerald-400' : 'text-emerald-600'}`}>{label}</label>
                    <input type="text" value={(editVerb.forms_latin || {})[key] || ''}
                      onChange={e => setEditVerb({ ...editVerb, forms_latin: { ...(editVerb.forms_latin || {}), [key]: e.target.value } })}
                      className={inputCls} placeholder="auto" />
                  </div>
                ))}
              </div>
            </div>
            {/* Per-pronoun Arabic forms */}
            <div className={`mb-4 p-3 rounded ${dk ? 'bg-gray-700' : 'bg-amber-50'}`}>
              <p className={`text-xs font-medium mb-2 ${dk ? 'text-amber-400' : 'text-amber-600'}`}>Arabic forms per pronoun (without ما prefix — system adds it):</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {PRONOUNS.map(({ key, label }) => (
                  <div key={`far-${key}`}>
                    <label className={`text-xs ${dk ? 'text-amber-400' : 'text-amber-600'}`}>{label}</label>
                    <input type="text" dir="rtl" value={(editVerb.forms_ar || {})[key] || ''}
                      onChange={e => setEditVerb({ ...editVerb, forms_ar: { ...(editVerb.forms_ar || {}), [key]: e.target.value } })}
                      className={inputClsAr} />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEditVerb(null)} className={`px-4 py-2 rounded ${dk ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}>Cancel</button>
              <button onClick={saveEditVerb} className="px-4 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Info Cards */}
      <div className={`mb-6 p-4 rounded-lg border-l-4 border-blue-500 ${dk ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
        <h4 className={`font-bold mb-2 ${dk ? 'text-blue-300' : 'text-blue-800'}`}>How This System Works</h4>
        <p className={`text-sm ${dk ? 'text-blue-200' : 'text-blue-700'}`}>
          Builds negative verb forms: <strong>ma + stem + suffix</strong>. Each group defines suffixes per pronoun. Each verb has a Darija stem (Latin + Arabic script).
          <br /><br />
          <strong>Handles:</strong> don't, doesn't, do not, does not, can't, cannot, couldn't, shouldn't, wouldn't, won't<br />
          <strong>Not yet:</strong> didn't, haven't, never (different verb forms needed)
        </p>
      </div>

      {/* Test Negation */}
      <div className={`mb-6 p-4 rounded-lg border ${dk ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
        <h4 className={`font-semibold mb-3 ${dk ? 'text-white' : 'text-gray-800'}`}>Test Negation</h4>
        <div className="flex gap-2">
          <input type="text" value={testStem} onChange={e => setTestStem(e.target.value)} placeholder="Enter verb stem (e.g., kl, qra, ktab)"
            className={`flex-1 px-3 py-2 rounded border ${dk ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`} />
          <button onClick={() => testStem.trim() && testNegation(testStem.trim())} className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700">Test</button>
        </div>
        {testResult && (
          <div className={`mt-3 p-3 rounded ${dk ? 'bg-gray-600' : 'bg-white'}`}>
            <p className={`text-sm font-medium mb-2 ${dk ? 'text-emerald-400' : 'text-emerald-700'}`}>
              {testResult.verb_stem} (Group: {testResult.group_name})
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.entries(testResult.forms || {}).map(([key, form]) => (
                <div key={key} className={`p-2 rounded text-sm ${dk ? 'bg-gray-500' : 'bg-gray-100'}`}>
                  <span className={`font-medium ${dk ? 'text-gray-300' : 'text-gray-500'}`}>{form.label}: </span>
                  <span className={`font-bold ${dk ? 'text-white' : 'text-gray-800'}`}>{form.negative}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create New Group */}
      <div className={`mb-6 p-4 rounded-lg border ${dk ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
        <h4 className={`font-semibold mb-3 ${dk ? 'text-white' : 'text-gray-800'}`}>Create New Group</h4>
        <input type="text" value={newGroup.name} onChange={e => setNewGroup({ ...newGroup, name: e.target.value })}
          placeholder="Group name" className={`w-full px-3 py-2 rounded border mb-3 ${dk ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`} />

        <p className={`text-sm mb-2 ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Latin suffixes (after "ma + stem"):</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          {PRONOUNS.map(({ key, label }) => (
            <div key={key}>
              <label className={`text-xs font-medium ${dk ? 'text-gray-400' : 'text-gray-500'}`}>{label}</label>
              <input type="text" value={newGroup.suffixes[key]}
                onChange={e => setNewGroup({ ...newGroup, suffixes: { ...newGroup.suffixes, [key]: e.target.value } })}
                placeholder="suffix" className={inputCls} />
            </div>
          ))}
        </div>

        <p className={`text-sm mb-2 ${dk ? 'text-amber-400' : 'text-amber-600'}`}>Arabic script suffixes (after "ما + stem"):</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          {PRONOUNS.map(({ key, label }) => (
            <div key={`ar-${key}`}>
              <label className={`text-xs font-medium ${dk ? 'text-amber-400' : 'text-amber-600'}`}>{label}</label>
              <input type="text" dir="rtl" value={newGroup.suffixes_ar[key]}
                onChange={e => setNewGroup({ ...newGroup, suffixes_ar: { ...newGroup.suffixes_ar, [key]: e.target.value } })}
                placeholder="لاحقة" className={inputClsAr} />
            </div>
          ))}
        </div>
        <button onClick={createGroup} className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700">Create Group</button>
      </div>

      {/* Existing Groups */}
      <h4 className={`font-semibold mb-3 ${dk ? 'text-white' : 'text-gray-800'}`}>Groups ({groups.length})</h4>
      {loading ? (
        <p className={dk ? 'text-gray-400' : 'text-gray-500'}>Loading...</p>
      ) : groups.length === 0 ? (
        <p className={dk ? 'text-gray-400' : 'text-gray-500'}>No groups yet.</p>
      ) : (
        <div className="space-y-4">
          {groups.map(group => (
            <div key={group.id} className={`p-4 rounded-lg border ${dk ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
              {/* Group Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {editGroupName === group.id ? (
                    <div className="flex gap-2">
                      <input type="text" defaultValue={group.name} id={`rename-${group.id}`}
                        className={`px-2 py-1 rounded border text-sm font-bold ${dk ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`} />
                      <button onClick={() => {
                        const el = document.getElementById(`rename-${group.id}`);
                        if (el && el.value.trim()) updateGroup(group.id, { name: el.value.trim() });
                      }} className="px-2 py-1 rounded text-sm bg-emerald-600 text-white">Save</button>
                      <button onClick={() => setEditGroupName(null)} className={`px-2 py-1 rounded text-sm ${dk ? 'bg-gray-600 text-gray-200' : 'bg-gray-200'}`}>Cancel</button>
                    </div>
                  ) : (
                    <h5 className={`font-bold text-lg cursor-pointer ${dk ? 'text-white' : 'text-gray-800'}`} onClick={() => setEditGroupName(group.id)} title="Click to rename">
                      {group.name}
                      <span className={`ml-2 text-sm font-normal ${dk ? 'text-gray-400' : 'text-gray-500'}`}>({(group.verbs || []).length} verbs)</span>
                    </h5>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setExpandedGroup(expandedGroup === group.id ? null : group.id)}
                    className={`px-3 py-1 rounded text-sm ${dk ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                    {expandedGroup === group.id ? 'Collapse' : 'Expand'}
                  </button>
                  <button onClick={() => setEditGroup(editGroup === group.id ? null : group.id)}
                    className="px-3 py-1 rounded text-sm bg-blue-600 text-white hover:bg-blue-700">Edit Suffixes</button>
                  <button onClick={() => deleteGroup(group.id)}
                    className="px-3 py-1 rounded text-sm bg-red-600 text-white hover:bg-red-700">Delete</button>
                </div>
              </div>

              {/* Suffix Display — Latin */}
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-1 mb-1">
                {PRONOUN_KEYS.map(key => (
                  <div key={key} className={`text-center p-1 rounded text-xs ${dk ? 'bg-gray-600' : 'bg-gray-100'}`}>
                    <div className={dk ? 'text-gray-400' : 'text-gray-500'}>
                      {key === 'you_sm' ? 'you(SM)' : key === 'you_sf' ? 'you(SF)' : key === 'you_p' ? 'you(P)' : key}
                    </div>
                    <div className={`font-mono font-bold ${dk ? 'text-emerald-400' : 'text-emerald-700'}`}>
                      {group.suffixes?.[key] || '-'}
                    </div>
                  </div>
                ))}
              </div>
              {/* Suffix Display — Arabic */}
              {group.suffixes_ar && Object.values(group.suffixes_ar).some(v => v) && (
                <div className="grid grid-cols-4 sm:grid-cols-8 gap-1 mb-3">
                  {PRONOUN_KEYS.map(key => (
                    <div key={`ar-${key}`} className={`text-center p-1 rounded text-xs ${dk ? 'bg-gray-600/50' : 'bg-amber-50'}`}>
                      <div className={`font-mono font-bold text-right ${dk ? 'text-amber-400' : 'text-amber-700'}`} dir="rtl">
                        {group.suffixes_ar?.[key] || '-'}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Edit Suffixes Form */}
              {editGroup === group.id && (
                <div className={`p-3 rounded mb-3 ${dk ? 'bg-gray-600' : 'bg-yellow-50'}`}>
                  <p className={`text-xs mb-2 font-medium ${dk ? 'text-gray-300' : 'text-gray-600'}`}>Latin suffixes:</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                    {PRONOUNS.map(({ key, label }) => (
                      <div key={key}>
                        <label className={`text-xs ${dk ? 'text-gray-300' : 'text-gray-600'}`}>{label}</label>
                        <input type="text" defaultValue={group.suffixes?.[key] || ''} id={`neg-edit-${group.id}-${key}`} className={inputCls} />
                      </div>
                    ))}
                  </div>
                  <p className={`text-xs mb-2 font-medium ${dk ? 'text-amber-400' : 'text-amber-600'}`}>Arabic script suffixes:</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                    {PRONOUNS.map(({ key, label }) => (
                      <div key={`ar-${key}`}>
                        <label className={`text-xs ${dk ? 'text-amber-400' : 'text-amber-600'}`}>{label}</label>
                        <input type="text" dir="rtl" defaultValue={group.suffixes_ar?.[key] || ''} id={`neg-edit-ar-${group.id}-${key}`} className={inputClsAr} />
                      </div>
                    ))}
                  </div>
                  <button onClick={() => {
                    const suffixes = {}, suffixes_ar = {};
                    PRONOUN_KEYS.forEach(key => {
                      const el = document.getElementById(`neg-edit-${group.id}-${key}`);
                      const elAr = document.getElementById(`neg-edit-ar-${group.id}-${key}`);
                      suffixes[key] = el ? el.value : group.suffixes?.[key] || '';
                      suffixes_ar[key] = elAr ? elAr.value : group.suffixes_ar?.[key] || '';
                    });
                    updateGroup(group.id, { suffixes, suffixes_ar });
                  }} className="px-3 py-1 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700">Save Suffixes</button>
                </div>
              )}

              {/* Expanded: Verbs + Add */}
              {expandedGroup === group.id && (
                <div>
                  {/* Add Verb */}
                  <div className={`mb-3 p-3 rounded ${dk ? 'bg-gray-600' : 'bg-gray-50'}`}>
                    <p className={`text-xs mb-2 font-medium ${dk ? 'text-gray-300' : 'text-gray-600'}`}>Add Verb:</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                      <div>
                        <label className={`text-xs ${dk ? 'text-gray-400' : 'text-gray-500'}`}>English</label>
                        <input type="text" value={addVerb.groupId === group.id ? addVerb.english : ''}
                          onChange={e => setAddVerb({ ...addVerb, groupId: group.id, english: e.target.value })}
                          placeholder="eat" className={inputCls} />
                      </div>
                      <div>
                        <label className={`text-xs ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Stem</label>
                        <input type="text" value={addVerb.groupId === group.id ? addVerb.stem : ''}
                          onChange={e => setAddVerb({ ...addVerb, groupId: group.id, stem: e.target.value })}
                          placeholder="kl" className={inputCls} />
                      </div>
                      <div>
                        <label className={`text-xs ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Fem stem</label>
                        <input type="text" value={addVerb.groupId === group.id ? addVerb.fem_stem || '' : ''}
                          onChange={e => setAddVerb({ ...addVerb, groupId: group.id, fem_stem: e.target.value })}
                          placeholder="optional" className={inputCls} />
                      </div>
                      <div>
                        <label className={`text-xs ${dk ? 'text-gray-400' : 'text-gray-500'}`}>Alt stem</label>
                        <input type="text" value={addVerb.groupId === group.id ? addVerb.alt_stem || '' : ''}
                          onChange={e => setAddVerb({ ...addVerb, groupId: group.id, alt_stem: e.target.value })}
                          placeholder="optional" className={inputCls} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                      <div>
                        <label className={`text-xs ${dk ? 'text-amber-400' : 'text-amber-600'}`}>Stem (Arabic)</label>
                        <input type="text" dir="rtl" value={addVerb.groupId === group.id ? addVerb.stem_ar || '' : ''}
                          onChange={e => setAddVerb({ ...addVerb, groupId: group.id, stem_ar: e.target.value })}
                          className={inputClsAr} />
                      </div>
                      <div>
                        <label className={`text-xs ${dk ? 'text-amber-400' : 'text-amber-600'}`}>Fem stem (Arabic)</label>
                        <input type="text" dir="rtl" value={addVerb.groupId === group.id ? addVerb.fem_stem_ar || '' : ''}
                          onChange={e => setAddVerb({ ...addVerb, groupId: group.id, fem_stem_ar: e.target.value })}
                          className={inputClsAr} />
                      </div>
                      <div>
                        <label className={`text-xs ${dk ? 'text-amber-400' : 'text-amber-600'}`}>Alt stem (Arabic)</label>
                        <input type="text" dir="rtl" value={addVerb.groupId === group.id ? addVerb.alt_stem_ar || '' : ''}
                          onChange={e => setAddVerb({ ...addVerb, groupId: group.id, alt_stem_ar: e.target.value })}
                          className={inputClsAr} />
                      </div>
                    </div>
                    <button onClick={() => addVerbToGroup(group.id)} className="px-4 py-2 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700">Add Verb</button>
                  </div>

                  {/* Verb List */}
                  {(group.verbs || []).length === 0 ? (
                    <p className={`text-sm ${dk ? 'text-gray-400' : 'text-gray-500'}`}>No verbs yet.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {(group.verbs || []).map(v => (
                        <div key={v.stem} className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${dk ? 'bg-gray-600' : 'bg-gray-100'}`}>
                          <span className={`font-mono font-bold ${dk ? 'text-emerald-400' : 'text-emerald-700'}`}>{v.stem}</span>
                          {v.stem_ar && <span className={`font-mono ${dk ? 'text-amber-400' : 'text-amber-600'}`}>({v.stem_ar})</span>}
                          {v.fem_stem && <span className={`font-mono ${dk ? 'text-pink-400' : 'text-pink-600'}`}>/f:{v.fem_stem}</span>}
                          {v.alt_stem && <span className={`font-mono ${dk ? 'text-blue-400' : 'text-blue-600'}`}>/a:{v.alt_stem}</span>}
                          {v.english && <span className={dk ? 'text-gray-400' : 'text-gray-500'}>({v.english})</span>}
                          <button onClick={() => setEditVerb({
                            groupId: group.id, originalStem: v.stem,
                            stem: v.stem, english: v.english || '', fem_stem: v.fem_stem || '', alt_stem: v.alt_stem || '',
                            stem_ar: v.stem_ar || '', fem_stem_ar: v.fem_stem_ar || '', alt_stem_ar: v.alt_stem_ar || '',
                            forms_ar: v.forms_ar || {},
                            forms_latin: v.forms_latin || {}
                          })} className={`ml-2 px-2 py-0.5 rounded ${dk ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'} text-xs`} title="Edit verb">Edit</button>
                          <button onClick={() => removeVerb(group.id, v.stem)}
                            className={`ml-1 px-2 py-0.5 rounded ${dk ? 'bg-red-600 text-white hover:bg-red-500' : 'bg-red-100 text-red-700 hover:bg-red-200'} text-xs`} title="Remove verb">Remove</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NegationGroupsAdmin;
